/**
 * Blackout / block-slot debugger — hours, API payload, indexing, paint.
 *
 * Always logs to console while investigating (toggle off with):
 *   localStorage.setItem('__BLACKOUT_DEBUG', '0'); location.reload();
 *
 * Keep on:
 *   localStorage.setItem('__BLACKOUT_DEBUG', '1'); location.reload();
 *
 * Console helpers:
 *   __blackoutDebug.help()
 *   __blackoutDebug.dump()
 *   __blackoutDebug.clear()
 *   __blackoutDebug.copy()
 */

const MAX_LOG = 500;
const PREFIX = "[BLACKOUT-DEBUG]";

const logBuffer = [];

function readFlag() {
   if (typeof window === "undefined") return false;
   if (window.__BLACKOUT_DEBUG === false) return false;
   if (window.__BLACKOUT_DEBUG === true) return true;
   try {
      const v = localStorage.getItem("__BLACKOUT_DEBUG");
      if (v === "0") return false;
      if (v === "1") return true;
   } catch {}
   // Quiet by default — enable with localStorage __BLACKOUT_DEBUG=1
   if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") {
      return false;
   }
   return false;
}

export function isBlackoutDebugEnabled() {
   return readFlag();
}

export function blackoutDebug(stage, payload = {}) {
   if (!readFlag()) return;
   const entry = {
      t: new Date().toISOString(),
      ms: typeof performance !== "undefined" ? performance.now() : Date.now(),
      stage: String(stage || ""),
      ...payload,
   };
   logBuffer.push(entry);
   if (logBuffer.length > MAX_LOG) logBuffer.splice(0, logBuffer.length - MAX_LOG);

   try {
      // eslint-disable-next-line no-console
      console.log(
         `%c${PREFIX}%c ${entry.stage}`,
         "color:#fff;background:#c0392b;padding:1px 4px;border-radius:3px",
         "color:#c0392b;font-weight:700",
         entry,
      );
   } catch {}
}

export function blackoutDebugWarn(stage, payload = {}) {
   if (!readFlag()) return;
   const entry = {
      t: new Date().toISOString(),
      stage: String(stage || ""),
      level: "warn",
      ...payload,
   };
   logBuffer.push(entry);
   try {
      // eslint-disable-next-line no-console
      console.warn(PREFIX, entry.stage, entry);
   } catch {}
}

/** Wall-clock datetime for blackout API: "YYYY-MM-DDTHH:mm:00Z" (no TZ shift). */
export function buildBlackoutWallClockDateTime(dayKey, slotLabel) {
   const day = String(dayKey || "").trim();
   const hmMatch = String(slotLabel || "")
      .trim()
      .match(/^(\d{1,2}):(\d{2})$/);
   if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !hmMatch) return "";
   const hh = String(Number(hmMatch[1])).padStart(2, "0");
   const mm = hmMatch[2];
   return `${day}T${hh}:${mm}:00Z`;
}

export function explainBlackoutDateTime(rawValue) {
   const raw = String(rawValue || "").trim();
   if (!raw) return { raw: "", note: "empty" };

   const digitParts = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
   );
   const rawWall = digitParts
      ? `${digitParts[1]}-${digitParts[2]}-${digitParts[3]}|${digitParts[4]}:${digitParts[5]}`
      : "";

   const parsed = new Date(raw);
   const hasParsed = !Number.isNaN(parsed.getTime());
   let moldovaFromUtc = "";
   if (hasParsed) {
      try {
         const parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/Chisinau",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
         }).formatToParts(parsed);
         const get = (type) => parts.find((p) => p.type === type)?.value || "";
         moldovaFromUtc = `${get("year")}-${get("month")}-${get("day")}|${get("hour")}:${get("minute")}`;
      } catch {}
   }

   const endsWithZ = /Z$/i.test(raw);
   const hasOffset = /[+-]\d{2}:\d{2}$/.test(raw);
   const hourShiftRisk =
      endsWithZ &&
      rawWall &&
      moldovaFromUtc &&
      rawWall !== moldovaFromUtc;

   return {
      raw,
      rawWallClockKey: rawWall,
      moldovaIfTreatedAsUtc: moldovaFromUtc,
      endsWithZ,
      hasNumericOffset: hasOffset,
      hourShiftRisk,
      note: hourShiftRisk
         ? "RISK: Z suffix treated as real UTC shifts Moldova hour"
         : "ok",
   };
}

function installConsoleApi() {
   if (typeof window === "undefined") return;
   window.__blackoutDebug = {
      help() {
         // eslint-disable-next-line no-console
         console.log(`
${PREFIX} help
  __blackoutDebug.dump()   — print all buffered events
  __blackoutDebug.copy()   — copy JSON to clipboard
  __blackoutDebug.clear()  — clear buffer
  localStorage __BLACKOUT_DEBUG = '1' | '0'  then reload
`);
      },
      dump() {
         // eslint-disable-next-line no-console
         console.table(
            logBuffer.map((e) => ({
               t: e.t,
               stage: e.stage,
               slotKey: e.slotKey || e.calendarSlotKey || "",
               dateTime: e.dateTime || e.startTimeToSend || e.blackoutDateTime || "",
               note: e.note || e.action || "",
            })),
         );
         // eslint-disable-next-line no-console
         console.log(PREFIX, "full buffer", logBuffer.slice());
         return logBuffer.slice();
      },
      clear() {
         logBuffer.length = 0;
         // eslint-disable-next-line no-console
         console.log(PREFIX, "cleared");
      },
      async copy() {
         const text = JSON.stringify(logBuffer, null, 2);
         try {
            await navigator.clipboard.writeText(text);
            // eslint-disable-next-line no-console
            console.log(PREFIX, "copied", logBuffer.length, "events");
         } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(PREFIX, "clipboard failed", error);
            // eslint-disable-next-line no-console
            console.log(text);
         }
         return text;
      },
      enable() {
         try {
            localStorage.setItem("__BLACKOUT_DEBUG", "1");
         } catch {}
         window.__BLACKOUT_DEBUG = true;
         // eslint-disable-next-line no-console
         console.log(PREFIX, "enabled — reload not required for new logs");
      },
      disable() {
         try {
            localStorage.setItem("__BLACKOUT_DEBUG", "0");
         } catch {}
         window.__BLACKOUT_DEBUG = false;
         // eslint-disable-next-line no-console
         console.log(PREFIX, "disabled");
      },
      get buffer() {
         return logBuffer;
      },
   };
}

installConsoleApi();
